import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL;
  const [loadingPdfId, setLoadingPdfId] = useState(null);
  const [todos, setTodos] = useState([]);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    { sender: 'ai', text: "Bonjour ! Je suis votre expert cyber dédié. Une question sur votre rapport ou besoin d'un document ?" }
  ]);
  const [inputMsg, setInputMsg] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [generatedModel, setGeneratedModel] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Déclaration de currentUser ajoutée ici pour éviter les erreurs de variables non définies
  const [currentUser, setCurrentUser] = useState(() => {
    if (user) return user;
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const MUTUACORE_API_URL = process.env.REACT_APP_MUTUACORE_API_URL || 'https://sso.mutuacore.fr';

  useEffect(() => {
    const chargerProfil = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(`${MUTUACORE_API_URL}/utilisateurs/moi`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const profil = await response.json();

          // 🔗 Réconciliation avec le compte local (id entier) : sans cet appel,
          // toutes les routes locales (history, ia-provider, generate-pdf, ask-ai,
          // todos, create-customer-portal) reçoivent l'UUID MutuaCore et échouent.
          let idLocal = null;
          let planLocal = profil.plan_name;
          try {
            const syncResponse = await fetch(`${API_URL}/sync-user`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                mutuacore_id: profil.user_id,
                email: profil.email,
                company: profil.company,
                plan_name: profil.plan_name
              })
            });
            if (syncResponse.ok) {
              const syncData = await syncResponse.json();
              idLocal = syncData.id;
              planLocal = syncData.plan_name || profil.plan_name;
            } else {
              console.error('Erreur synchronisation compte local:', await syncResponse.text());
            }
          } catch (syncErr) {
            console.error('Erreur réseau synchronisation compte local:', syncErr);
          }

          const userData = {
            id: idLocal,
            mutuacore_id: profil.user_id,
            email: profil.email,
            company: profil.company,
            plan_name: planLocal,
            nom: profil.nom,
            prenom: profil.prenom,
          };
          setCurrentUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        }
      } catch (err) {
        console.error("Erreur chargement profil MutuaCore:", err);
      }
    };

    chargerProfil();
  }, [MUTUACORE_API_URL, API_URL]);

  // 🧠 MOTEUR DE RÈGLES DES OFFRES (CYBER & IA ACT)
  const planActuel = currentUser?.plan_name || "Starter";
  const planMin = planActuel.toLowerCase();

  let maxModels = 0;
  let hasDynamicPlan = false;
  let maxBotQuestions = 100;
  let isSubscription = false;
  let isElite = false;

  // 👑 NIVEAU 3 : Elite & SOC IA Continu
  if (planMin.includes("elite") || planMin.includes("soc ia")) {
    maxModels = Infinity;
    hasDynamicPlan = true;
    maxBotQuestions = Infinity;
    isSubscription = true; // Active la pastille verte "Suivi Continu"
    isElite = true;
  }
  // ⭐ NIVEAU 2 : Premium & Audit 360° Deepfake
  else if (planMin.includes("premium") || planMin.includes("360") || planMin.includes("deepfake")) {
    maxModels = 15;
    hasDynamicPlan = true;
    maxBotQuestions = 500;
    isSubscription = planMin.includes("premium"); // Seul le Premium historique était un abo
  }
  // 🛡️ NIVEAU 1.5 : Audit Complet
  else if (planMin.includes("complet")) {
    maxModels = 5;
    hasDynamicPlan = true;
    maxBotQuestions = 100;
  }
  // 🔍 NIVEAU 1 : Diagnostic Pro (99€) - ATTENTION: Exclut "Diagnostic Légal"
  else if (planMin.includes("diagnostic pro")) {
    maxModels = 2;
    hasDynamicPlan = true;
    maxBotQuestions = 100;
  }
  // 🆓 NIVEAU 0 : Starter (49€) / Diagnostic Légal (49€) / Découverte (0€)
  else {
    maxModels = 0;
    hasDynamicPlan = false;
    maxBotQuestions = 100;
  }

  useEffect(() => {
    if (user) setCurrentUser(user);
  }, [user]);

  useEffect(() => {
    if (currentUser && currentUser.id) {
      fetch(`${API_URL}/history?user_id=${currentUser.id}`)
        .then(res => res.json())
        .then(data => { if (!data.error) setHistory(data); })
        .catch(err => console.error("Erreur historique:", err));

      if (hasDynamicPlan) {
        fetch(`${API_URL}/todos?user_id=${currentUser.id}`)
          .then(res => res.json())
          .then(data => setTodos(data))
          .catch(err => console.error("Erreur todos:", err));
      } else {
        // Tâches factices pour donner envie (Upsell)
        setTodos([
          { id: 'fake1', text: "Mettre à jour le registre RGPD", done: false },
          { id: 'fake2', text: "Activer le MFA sur les comptes admin", done: false },
          { id: 'fake3', text: "Créer une charte informatique", done: false }
        ]);
      }
    }
  }, [currentUser?.id, hasDynamicPlan, API_URL]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/create-customer-portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: currentUser.id })
      });
      const data = await response.json();

      if (data.url) window.location.href = data.url;
      else alert("Redirection vers le portail Stripe.");
    } catch (err) {
      alert("Le portail Stripe sera bientôt connecté.");
    }
  };

  const toggleTodo = async (id, currentStatus) => {
    if (!hasDynamicPlan) return alert("🔒 Passez au moins à l'offre Diagnostic Pro (99€) ou Audit 360° pour activer et suivre votre plan d'action dynamique !");

    setTodos(todos.map(t => t.id === id ? { ...t, done: !currentStatus } : t));
    try {
      await fetch(`${API_URL}/toggle-todo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ todo_id: id, done: !currentStatus })
      });
    } catch (error) {
      console.error("Erreur de sauvegarde de la tâche");
    }
  };

  const handleGenerateModel = async (todoText) => {
    if (!hasDynamicPlan) return alert("🔒 Passez à l'offre Pro/360° pour débloquer la génération de modèles.");

    const usageKey = `model_usage_${currentUser.id}`;
    let countModels = parseInt(localStorage.getItem(usageKey) || "0");

    if (countModels >= maxModels && !isElite) {
      if (planMin.includes("360") || planMin.includes("premium")) {
        alert("🔒 Limite atteinte (15/15 modèles). Passez à l'abonnement SOC IA (490€) pour l'illimité ! 🚀");
      } else {
        alert("🔒 Limite atteinte. Passez à l'offre supérieure pour plus de modèles ! 🚀");
      }
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(`${API_URL}/generate-model`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ todo_text: todoText })
      });
      const data = await response.json();

      if (data.error) {
        alert("Erreur: " + data.error);
      } else {
        setGeneratedModel({ title: todoText, text: data.model_text });
        if (!isElite) localStorage.setItem(usageKey, countModels + 1);
      }
    } catch (err) {
      alert("Erreur de connexion lors de la génération.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMsg.trim()) return;

    const currentMonth = new Date().getMonth();
    const usageKey = `chat_usage_${currentUser.id}_${currentMonth}`;
    let countMessages = parseInt(localStorage.getItem(usageKey) || "0");

    if (countMessages >= maxBotQuestions && !isElite) {
      const limitMsg = {
        sender: 'ai',
        text: planMin.includes("360") || planMin.includes("premium")
          ? "Vous avez atteint votre limite d'usage étendu.\n\nPassez à l'offre SOC IA pour l'illimité absolu ! 🚀"
          : "Vous avez atteint votre limite de questions.\n\nPassez à l'abonnement pour débloquer un usage étendu ! 🚀"
      };
      setMessages([...messages, { sender: 'user', text: inputMsg }, limitMsg]);
      setInputMsg('');
      return;
    }

    setMessages([...messages, { sender: 'user', text: inputMsg }]);
    setInputMsg('');
    setIsTyping(true);

    if (!isElite) localStorage.setItem(usageKey, countMessages + 1);

    try {
      const response = await fetch(`${API_URL}/ask-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: inputMsg, user_id: currentUser.id })
      });
      const data = await response.json();
      setMessages(prev => [...prev, { sender: 'ai', text: String(data.response || data.reponse || "Je n'ai pas compris.") }]);
    } catch (error) {
      setMessages(prev => [...prev, { sender: 'ai', text: "Erreur de connexion avec le serveur IA 🔌." }]);
    } finally {
      setIsTyping(false);
    }
  };

  const downloadPastReport = async (audit) => {
    if (!currentUser || !currentUser.id) return alert("Veuillez vous reconnecter.");
    setLoadingPdfId(audit.id);

    try {
      const response = await fetch(`${API_URL}/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score: audit.score, reponses: audit.responses, entreprise: currentUser.company, user_id: currentUser.id }),
      });

      if (response.status === 403) {
        const errorData = await response.json();
        alert(errorData.message);
        navigate('/pricing');
        return;
      }
      if (!response.ok) throw new Error("Erreur de génération");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Rapport_Expert_${new Date(audit.date).toLocaleDateString().replace(/\//g, '-')}.pdf`;
      a.click();
    } catch (error) {
      alert("Erreur lors de la génération du PDF.");
    } finally {
      setLoadingPdfId(null);
    }
  };

  if (!currentUser) return <div style={{ padding: '40px', textAlign: 'center' }}>Chargement...</div>;

  const statsGraphique = [...history].reverse().slice(-6);

  // 📈 MOTEUR DU SCORE DE SANTÉ DYNAMIQUE
  const baseScore = history.length > 0 ? parseInt(history[0].score) : 0;
  let scoreSante = "--";

  if (history.length > 0) {
    if (todos.length > 0 && hasDynamicPlan) {
      const pointsManquants = 100 - baseScore;
      const valeurParTodo = pointsManquants / todos.length;
      const todosTermines = todos.filter(t => t.done).length;
      scoreSante = Math.min(100, Math.round(baseScore + (valeurParTodo * todosTermines)));
    } else {
      scoreSante = baseScore;
    }
  }

  return (
    <div style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto', fontFamily: '"Inter", sans-serif', backgroundColor: '#f8f9fa', minHeight: '100vh', position: 'relative' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h1 style={{ margin: 0, color: '#2c3e50', fontSize: '28px' }}>Tableau de Bord : {currentUser.company} 🚀</h1>
          <p style={{ color: '#7f8c8d' }}>Pilotez votre cyber-résilience en temps réel.</p>
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
          <button onClick={() => navigate('/audit')} style={{ padding: '12px 20px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 10px rgba(52, 152, 219, 0.2)' }}>+ Lancer un nouvel Audit</button>
          <button onClick={handleLogout} style={{ padding: '12px 20px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 10px rgba(231, 76, 60, 0.2)' }}>Se déconnecter</button>
        </div>
      </div>

      {!isSubscription && history.length > 0 && (
        <div style={{ background: 'linear-gradient(90deg, #f39c12 0%, #e67e22 100%)', color: 'white', padding: '20px', borderRadius: '12px', marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 5px 15px rgba(230, 126, 34, 0.3)' }}>
          <div>
            <h3 style={{ margin: '0 0 5px 0', fontSize: '18px' }}>Passez à la vitesse supérieure ⚡</h3>
            <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>Automatisez vos corrections avec le suivi SOC IA Continu.</p>
          </div>
          <button onClick={() => navigate('/pricing')} style={{ padding: '10px 20px', background: '#fff', color: '#d35400', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Voir les abonnements</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>

        <div style={{ background: 'white', padding: '20px', borderRadius: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '13px', color: '#7f8c8d', fontWeight: 'bold' }}>SCORE DE SANTÉ</div>
          <div style={{ fontSize: '42px', fontWeight: 'bold', color: scoreSante < 50 ? '#e74c3c' : (scoreSante < 80 ? '#f39c12' : '#27ae60'), transition: 'color 0.3s ease' }}>
            {scoreSante}{scoreSante !== "--" ? "%" : ""}
          </div>
          {history.length > 0 && (
            <button onClick={() => downloadPastReport(history[0])} disabled={loadingPdfId === history[0].id} style={{ marginTop: '10px', padding: '8px 15px', background: loadingPdfId === history[0].id ? '#95a5a6' : '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: loadingPdfId === history[0].id ? 'wait' : 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
              {loadingPdfId === history[0].id ? "⏳ L'IA rédige votre rapport (patientez ~1 min)" : '📄 Télécharger le Rapport'}
            </button>
          )}
        </div>

        <div style={{ background: 'white', padding: '20px', borderRadius: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '13px', color: '#7f8c8d', fontWeight: 'bold' }}>VOTRE OFFRE</div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', margin: '10px 0', color: isElite ? '#8e44ad' : '#2c3e50' }}>{planActuel}</div>
          {isSubscription ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <span style={{ padding: '4px 10px', background: '#dcfce7', color: '#166534', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' }}>ACTIF</span>
              <button onClick={handleManageSubscription} style={{ padding: '5px 12px', background: 'transparent', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', transition: 'all 0.2s' }}>Gérer / Résilier</button>
            </div>
          ) : (
            <span style={{ padding: '4px 10px', background: '#f1f5f9', color: '#64748b', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' }}>SANS ENGAGEMENT</span>
          )}
        </div>

        <div style={{ background: 'white', padding: '20px', borderRadius: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '13px', color: '#7f8c8d', fontWeight: 'bold' }}>SUIVI EN CONTINU</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '15px 0' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: isSubscription ? '#2ecc71' : '#e74c3c', boxShadow: isSubscription ? '0 0 10px #2ecc71' : 'none' }}></div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: isSubscription ? '#27ae60' : '#c0392b' }}>{isSubscription ? 'Actif' : 'Inactif'}</div>
          </div>
          <div style={{ fontSize: '11px', color: '#95a5a6' }}>{isSubscription ? `Dernier scan : ${new Date().toLocaleDateString()}` : '🔒 Réservé aux abonnements Premium/SOC IA'}</div>
        </div>
      </div>

      <div style={{ background: 'linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)', borderRadius: '15px', padding: '30px', color: 'white', marginBottom: '30px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          <span style={{ fontSize: '32px', marginRight: '15px' }}>👑</span>
          <div>
            <h2 style={{ margin: 0, color: '#f1c40f' }}>{isElite ? "Espace Elite : Stratégie & Suivi" : "Espace Accompagnement Continu"}</h2>
            <p style={{ margin: '5px 0 0 0', color: '#bdc3c7', fontSize: '14px' }}>Vos outils d'amélioration continue pour passer à 100%.</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>

          {/* LE BLOC PLAN D'ACTION (AVEC UPSELL) */}
          <div style={{ background: 'rgba(255,255,255,0.1)', padding: '20px', borderRadius: '12px', position: 'relative' }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#fff' }}>🎯 Plan d'Action Dynamique</h3>
            {todos.map(todo => (
              <div key={todo.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.05)', padding: '10px 15px', marginBottom: '10px', borderRadius: '8px', opacity: hasDynamicPlan ? 1 : 0.6 }}>
                <div onClick={() => toggleTodo(todo.id, todo.done)} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1 }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid #f1c40f', marginRight: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: todo.done ? '#f1c40f' : 'transparent' }}>
                    {todo.done && <span style={{ color: '#1a252f', fontSize: '12px', fontWeight: 'bold' }}>✓</span>}
                  </div>
                  <span style={{ textDecoration: todo.done ? 'line-through' : 'none', color: todo.done ? '#95a5a6' : '#fff', fontSize: '14px', transition: 'color 0.3s' }}>{todo.text}</span>
                </div>
                {hasDynamicPlan ? (
                  <button onClick={() => handleGenerateModel(todo.text)} disabled={isGenerating} style={{ marginLeft: '10px', fontSize: '11px', padding: '6px 10px', background: isGenerating ? '#7f8c8d' : '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>{isGenerating ? "Création..." : "📄 Générer modèle"}</button>
                ) : (
                  <button onClick={() => alert("🔒 Passez à l'offre Diagnostic Pro (99€) ou Audit 360° pour débloquer les modèles !")} style={{ marginLeft: '10px', fontSize: '11px', padding: '6px 10px', background: 'rgba(255,255,255,0.2)', color: '#bdc3c7', border: '1px solid #7f8c8d', borderRadius: '4px', cursor: 'pointer' }}>🔒 Modèle</button>
                )}
              </div>
            ))}

            {/* MESSAGE D'UPSELL MAGNIFIQUE SI LE PLAN EST BLOQUÉ */}
            {!hasDynamicPlan && (
              <div style={{ marginTop: '20px', textAlign: 'center', background: 'rgba(231, 76, 60, 0.15)', padding: '20px', borderRadius: '8px', border: '1px solid rgba(231, 76, 60, 0.4)' }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#ff7675', fontWeight: 'bold' }}>🔒 Débloquez le Plan d'Action</p>
                <p style={{ margin: '0 0 15px 0', fontSize: '13px', color: '#bdc3c7' }}>Passez à l'offre Pro ou 360° pour savoir exactement comment corriger vos failles.</p>
                <button onClick={() => navigate('/pricing')} style={{ padding: '10px 20px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', boxShadow: '0 4px 10px rgba(231, 76, 60, 0.2)' }}>
                  Voir les offres supérieures
                </button>
              </div>
            )}
          </div>

          <div style={{ background: 'rgba(255,255,255,0.1)', padding: '20px', borderRadius: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#fff' }}>🤖 Consultant IA Dédié</h3>
              <p style={{ fontSize: '13px', color: '#bdc3c7', margin: '0 0 20px 0' }}>{isElite ? "Posez vos questions en illimité." : "Analysez vos failles et posez vos questions à l'IA spécialisée."}</p>
            </div>
            <button onClick={() => setIsChatOpen(true)} style={{ width: '100%', padding: '12px', background: '#f1c40f', color: '#1a252f', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>Ouvrir le Chat Sécurisé</button>
          </div>

          <div style={{ background: 'rgba(255,255,255,0.1)', padding: '20px', borderRadius: '12px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#fff' }}>🛡️ Sceau de Confiance</h3>
            {baseScore >= 70 && isElite ? (
              <div>
                <p style={{ fontSize: '13px', color: '#bdc3c7', margin: '0 0 15px 0' }}>Félicitations, affichez ce badge sur votre site web !</p>
                <div style={{ background: '#1a252f', padding: '10px', borderRadius: '8px', textAlign: 'center', border: '1px solid #f1c40f' }}>
                  <span style={{ color: '#f1c40f', fontWeight: 'bold', fontSize: '14px' }}>✓ Audit Cyber Pro - Valide</span>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '10px' }}>
                <span style={{ fontSize: '30px' }}>🔒</span>
                <p style={{ fontSize: '13px', color: '#95a5a6', margin: '10px 0 0 0' }}>{baseScore < 70 ? "Atteignez un score de 70% pour débloquer votre badge public." : "Réservé aux clients Elite / SOC IA (490€)."}</p>
              </div>
            )}
          </div>

        </div>
      </div>

      {isChatOpen && (
        <div style={{ position: 'fixed', bottom: '30px', right: '30px', width: '350px', background: 'white', borderRadius: '15px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 1000, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #eee' }}>

          <div style={{ background: '#2c3e50', color: 'white', padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>🤖 Expert Cyber IA</h3>
            <button onClick={() => setIsChatOpen(false)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' }}>×</button>
          </div>

          <div style={{ padding: '15px', height: '350px', overflowY: 'auto', background: '#fdfdfd', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {messages.map((msg, idx) => <div key={idx} style={{ alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start', background: msg.sender === 'user' ? '#3498db' : '#ecf0f1', color: msg.sender === 'user' ? 'white' : '#2c3e50', padding: '10px 15px', borderRadius: '15px', maxWidth: '80%', fontSize: '14px', whiteSpace: 'pre-wrap' }}>{msg.text}</div>)}
            {isTyping && <div style={{ alignSelf: 'flex-start', background: '#ecf0f1', padding: '10px 15px', borderRadius: '15px', fontSize: '12px', color: '#7f8c8d' }}>L'IA analyse...</div>}
          </div>

          <div style={{ padding: '15px', borderTop: '1px solid #eee', display: 'flex', gap: '10px', background: 'white' }}>
            <input type="text" value={inputMsg} onChange={(e) => setInputMsg(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()} placeholder="Posez votre question..." style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none' }} />
            <button onClick={handleSendMessage} disabled={isTyping} style={{ padding: '10px 15px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Envoyer</button>
          </div>

          <div style={{ padding: '5px 15px 10px', background: 'white', textAlign: 'center', fontSize: '10px', color: '#95a5a6' }}>
            ⚠️ Réponses générées par une IA. Elles ne remplacent pas un conseil professionnel. Vérification recommandée.
          </div>

        </div>
      )}

      {generatedModel && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '15px', maxWidth: '700px', width: '90%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <h2 style={{ color: '#2c3e50', marginTop: 0, borderBottom: '2px solid #f1f5f9', paddingBottom: '15px' }}>Modèle : {generatedModel.title}</h2>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: '"Inter", sans-serif', fontSize: '14px', lineHeight: '1.6', color: '#34495e', background: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #eee' }}>{generatedModel.text}</pre>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button onClick={() => window.print()} style={{ flex: 1, padding: '12px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>🖨️ Imprimer / Sauvegarder en PDF</button>
                <button onClick={() => setGeneratedModel(null)} style={{ padding: '12px 25px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
        <h3 style={{ marginTop: 0, marginBottom: '40px', color: '#2c3e50' }}>Évolution de votre sécurité</h3>
        {history.length < 2 ? (
          <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #eee', borderRadius: '10px', color: '#bdc3c7' }}>Effectuez au moins 2 audits pour voir votre courbe.</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: '250px', paddingBottom: '30px', borderBottom: '2px solid #f1f5f9', position: 'relative' }}>
            {statsGraphique.map((audit, idx) => (
              <div key={idx} style={{ width: '12%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '100%', height: `${audit.score}px`, maxHeight: '200px', background: audit.score < 50 ? 'linear-gradient(to top, #e74c3c, #ff7675)' : 'linear-gradient(to top, #27ae60, #55efc4)', borderRadius: '6px 6px 0 0', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '-25px', width: '100%', textAlign: 'center', fontWeight: 'bold', fontSize: '12px' }}>{audit.score}%</div>
                </div>
                <div style={{ fontSize: '10px', color: '#95a5a6', whiteSpace: 'nowrap' }}>{new Date(audit.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ background: 'white', borderRadius: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div style={{ padding: '20px', borderBottom: '1px solid #eee', background: '#fcfcfc' }}>
          <h3 style={{ margin: 0 }}>Rapports PDF & Historique</h3>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #eee' }}>
                <th style={{ padding: '15px 20px', color: '#7f8c8d', fontSize: '13px' }}>DATE</th>
                <th style={{ padding: '15px 20px', color: '#7f8c8d', fontSize: '13px' }}>RÉSULTAT INITIAL</th>
                <th style={{ padding: '15px 20px', color: '#7f8c8d', fontSize: '13px', textAlign: 'right' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {history.map((audit) => (
                <tr key={audit.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                  <td style={{ padding: '15px 20px', fontWeight: '500' }}>{new Date(audit.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td style={{ padding: '15px 20px' }}>
                    <span style={{ padding: '4px 12px', borderRadius: '20px', background: audit.score < 50 ? '#fee2e2' : '#dcfce7', color: audit.score < 50 ? '#b91c1c' : '#166534', fontWeight: 'bold', fontSize: '13px' }}>{audit.score}%</span>
                  </td>
                  <td style={{ padding: '15px 20px', textAlign: 'right' }}>
                    <button onClick={() => downloadPastReport(audit)} disabled={loadingPdfId === audit.id} style={{ padding: '7px 15px', cursor: loadingPdfId === audit.id ? 'wait' : 'pointer', borderRadius: '6px', border: '1px solid #ddd', background: loadingPdfId === audit.id ? '#f1f5f9' : '#fff', color: loadingPdfId === audit.id ? '#94a3b8' : '#2c3e50' }}>
                      {loadingPdfId === audit.id ? "⏳ Patientez (~1 min)..." : '📄 Télécharger PDF'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
        </table>
      </div>

      <Footer />
    </div>
  );
}

export default Dashboard;